import {
  BroadviewClientError,
  createBroadviewClient,
  createBroadviewFixtureDataset,
  type BroadviewApiClient,
  type FixtureAccessScenario,
  type TenantAccess,
} from "@broadview/api-client";
import type { TenantContext } from "@broadview/api-client";
import { QueryClient } from "@tanstack/react-query";
import {
  ArrowRight,
  Building2,
  CheckCircle2,
  CircleAlert,
  KeyRound,
  LockKeyhole,
  Mail,
  ShieldCheck,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { RouterProvider } from "@tanstack/react-router";
import { configurePortalClient } from "./lib/portal-fixtures";
import { isInvalidCredentialsError, type PortalAuthClient } from "./lib/portal-auth";
import { automaticallySelectedDivision } from "./lib/division-selection";
import {
  divisionContextQueryOptions,
  divisionsQueryOptions,
  identityQueryOptions,
} from "./lib/portal-queries";
import { PortalRuntimeProvider, type PortalProblemRecordReader } from "./lib/portal-runtime";
import { getRouter } from "./router";

type AccessScreen =
  "sign-in" | "password-recovery" | "checking" | "tenant-selection" | "no-access" | "workspace";
type SignInMethod = "magic-link" | "password";

function scenarioFromLocation(): FixtureAccessScenario {
  const candidate = new URLSearchParams(window.location.search).get("fixture");
  if (candidate === "multiple") return "multiple-tenants";
  if (candidate === "none") return "no-access";
  return "single-tenant";
}

export function PortalBootstrap({
  client: injectedClient,
  auth,
  problemRecords,
  claims,
}: Readonly<{
  client?: BroadviewApiClient;
  auth?: PortalAuthClient;
  problemRecords?: PortalProblemRecordReader;
  claims?: import("./lib/portal-runtime").PortalRuntime["claims"];
}>) {
  const scenario = useMemo(scenarioFromLocation, []);
  const fixtureClient = useMemo(
    () =>
      createBroadviewClient({
        fixtures: createBroadviewFixtureDataset({ scenario }),
      }),
    [scenario],
  );
  const client = injectedClient ?? fixtureClient;
  const [queryClient] = useState(
    () => new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: true } } }),
  );
  const router = useMemo(() => getRouter(queryClient), [queryClient]);
  const [screen, setScreen] = useState<AccessScreen>("sign-in");
  const [tenants, setTenants] = useState<readonly TenantAccess[]>([]);
  const [tenantContext, setTenantContext] = useState<TenantContext | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [credentialFailure, setCredentialFailure] = useState(false);
  const [canRetryWorkspace, setCanRetryWorkspace] = useState(false);
  const generation = useRef(0);
  const subject = useRef<string | null | undefined>(undefined);
  const resolutionInFlight = useRef<number | null>(null);
  const workspaceReady = useRef(false);
  const recoveryActive = useRef(false);
  const divisionSelection = useRef(0);

  function resetWorkspace(nextSubject: string | null | undefined) {
    generation.current += 1;
    subject.current = nextSubject;
    resolutionInFlight.current = null;
    workspaceReady.current = false;
    recoveryActive.current = false;
    divisionSelection.current += 1;
    setScreen("sign-in");
    setTenantContext(null);
    setTenants([]);
    setMessage(null);
    setCredentialFailure(false);
    setCanRetryWorkspace(false);
    // clear() destroys/cancels old queries. Generation guards also protect
    // bootstrap continuations outside the query cache.
    queryClient.clear();
    configurePortalClient(client);
  }

  useEffect(() => {
    resetWorkspace(undefined);
    if (client.mode !== "live" || !auth) return;

    let cancelled = false;
    const timers = new Set<ReturnType<typeof setTimeout>>();
    const initialGeneration = generation.current;
    const passwordRecoveryCallback = auth.isPasswordRecoveryCallback();
    recoveryActive.current = passwordRecoveryCallback;
    if (passwordRecoveryCallback) {
      setMessage(null);
      setScreen("checking");
    }
    const resolveSession = () => {
      const expectedGeneration = generation.current;
      // Supabase holds its auth lock during notifications. Return synchronously
      // and start token-reading API work only after that callback has completed.
      const timer = setTimeout(() => {
        timers.delete(timer);
        if (!cancelled && generation.current === expectedGeneration && !recoveryActive.current)
          void resolveAuthenticatedWorkspace();
      }, 0);
      timers.add(timer);
    };
    const unsubscribe = auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      const nextSubject = session?.user.id ?? null;
      const changed = subject.current !== nextSubject;
      const passwordRecovery =
        event === "PASSWORD_RECOVERY" ||
        (recoveryActive.current && (!changed || subject.current === undefined));
      if (changed || !session || event === "PASSWORD_RECOVERY") resetWorkspace(nextSubject);
      if (!session) {
        if (passwordRecovery) {
          setMessage(
            "This recovery link could not restore a verified session. Request one new password setup link and discard older emails.",
          );
        }
        return;
      }
      if (passwordRecovery) {
        recoveryActive.current = true;
        setMessage(null);
        setScreen("password-recovery");
        return;
      }
      // Same-subject refresh/sign-in notifications must not discard a workspace.
      if (changed || !workspaceReady.current) resolveSession();
    });
    void auth
      .hasSession()
      .then((active) => {
        if (cancelled || generation.current !== initialGeneration) return;
        if (passwordRecoveryCallback) {
          if (active) {
            setMessage(null);
            setScreen("password-recovery");
          } else {
            recoveryActive.current = false;
            setScreen("sign-in");
            setMessage(
              "This recovery link could not restore a verified session. Request one new password setup link and discard older emails.",
            );
          }
          return;
        }
        if (active) resolveSession();
      })
      .catch(() => {
        if (!cancelled && generation.current === initialGeneration) resetWorkspace(null);
      });
    return () => {
      cancelled = true;
      unsubscribe();
      for (const timer of timers) clearTimeout(timer);
      generation.current += 1;
      resolutionInFlight.current = null;
      workspaceReady.current = false;
      queryClient.clear();
    };
  }, [auth, client, queryClient]); // eslint-disable-line react-hooks/exhaustive-deps

  async function resolveAuthenticatedWorkspace(): Promise<boolean> {
    const expectedGeneration = generation.current;
    if (
      recoveryActive.current ||
      (client.mode === "live" && auth && subject.current === null) ||
      resolutionInFlight.current === expectedGeneration
    )
      return false;
    if (workspaceReady.current) return true;
    resolutionInFlight.current = expectedGeneration;
    try {
      setMessage(null);
      setCredentialFailure(false);
      setCanRetryWorkspace(false);
      setScreen("checking");
      const [identity, memberships] = await Promise.all([
        queryClient.fetchQuery(identityQueryOptions(client)),
        queryClient.fetchQuery(divisionsQueryOptions(client)),
      ]);
      if (generation.current !== expectedGeneration) return false;
      if (subject.current != null && identity.userId !== subject.current) {
        throw new BroadviewClientError({
          status: 401,
          code: "UNAUTHENTICATED",
          message: "The authenticated account changed while resolving workspace access.",
        });
      }
      subject.current = identity.userId;
      setTenants(memberships);
      if (memberships.length === 0) {
        setScreen("no-access");
        return true;
      }
      const soleDivision = automaticallySelectedDivision(memberships);
      if (soleDivision) {
        await enterWorkspace(soleDivision, expectedGeneration);
        return generation.current === expectedGeneration;
      }
      setScreen("tenant-selection");
      return true;
    } catch (error) {
      if (generation.current !== expectedGeneration) return false;
      setScreen("sign-in");
      setMessage(accessMessage(error));
      const canRetry = client.mode === "live" && auth ? await auth.hasSession() : false;
      if (generation.current === expectedGeneration) setCanRetryWorkspace(canRetry);
      return false;
    } finally {
      if (resolutionInFlight.current === expectedGeneration) resolutionInFlight.current = null;
    }
  }

  async function continueToTenantResolution(credentials?: {
    method: SignInMethod;
    email: string;
    password: string;
  }): Promise<boolean> {
    const expectedGeneration = generation.current;
    try {
      setMessage(null);
      if (client.mode === "live" && credentials) {
        if (!auth) throw new Error("Live authentication is not configured.");
        if (credentials.method === "magic-link") {
          await auth.sendMagicLink(credentials.email, window.location.origin);
          if (generation.current !== expectedGeneration) return false;
          setMessage("Check your email for a secure Broadview sign-in link.");
          setScreen("sign-in");
          return true;
        }
        await auth.signInWithPassword({
          email: credentials.email,
          password: credentials.password,
        });
      }
      return await resolveAuthenticatedWorkspace();
    } catch (error) {
      if (generation.current !== expectedGeneration) return false;
      setScreen("sign-in");
      if (credentials?.method === "password" && isInvalidCredentialsError(error)) {
        setMessage(null);
        setCredentialFailure(true);
      } else {
        setMessage(accessMessage(error));
      }
      return false;
    }
  }

  async function requestPasswordRecovery(email: string): Promise<boolean> {
    const expectedGeneration = generation.current;
    try {
      setMessage(null);
      if (client.mode !== "live" || !auth) {
        throw new Error("Live authentication is not configured.");
      }
      await auth.requestPasswordRecovery(email, window.location.origin);
      return generation.current === expectedGeneration;
    } catch (error) {
      if (generation.current === expectedGeneration) setMessage(accessMessage(error));
      return false;
    }
  }

  async function updateRecoveredPassword(password: string) {
    const expectedGeneration = generation.current;
    try {
      setMessage(null);
      if (client.mode !== "live" || !auth) {
        throw new Error("Live authentication is not configured.");
      }
      await auth.updatePassword(password);
      if (generation.current !== expectedGeneration) return;
      recoveryActive.current = false;
      await resolveAuthenticatedWorkspace();
    } catch (error) {
      if (generation.current === expectedGeneration) setMessage(accessMessage(error));
    }
  }

  async function enterWorkspace(tenant: TenantAccess, expectedGeneration = generation.current) {
    const selection = ++divisionSelection.current;
    try {
      const context = await queryClient.fetchQuery(
        divisionContextQueryOptions(client, tenant.tenantId),
      );
      if (generation.current !== expectedGeneration || divisionSelection.current !== selection)
        return;
      configurePortalClient(client, tenant.tenantId);
      setTenantContext(context);
      workspaceReady.current = true;
      setScreen("workspace");
    } catch (error) {
      if (generation.current !== expectedGeneration || divisionSelection.current !== selection)
        return;
      setScreen("sign-in");
      setMessage(accessMessage(error));
    }
  }

  async function signOut() {
    const expectedGeneration = generation.current;
    if (client.mode === "live" && auth) await auth.signOut();
    if (generation.current === expectedGeneration) resetWorkspace(null);
  }

  if (screen === "workspace" && tenantContext) {
    return (
      <PortalRuntimeProvider
        runtime={{
          client,
          mode: client.mode,
          tenantContext,
          problemRecords,
          claims,
          auth,
          signOut,
        }}
      >
        <RouterProvider router={router} />
      </PortalRuntimeProvider>
    );
  }
  if (screen === "checking")
    return (
      <AccessFrame>
        <LoadingState />
      </AccessFrame>
    );
  if (screen === "tenant-selection") {
    return (
      <AccessFrame>
        <DivisionSelection tenants={tenants} onSelect={enterWorkspace} />
      </AccessFrame>
    );
  }
  if (screen === "no-access")
    return (
      <AccessFrame>
        <NoAccess onReturn={() => setScreen("sign-in")} />
      </AccessFrame>
    );
  if (screen === "password-recovery")
    return (
      <AccessFrame>
        <PasswordRecovery
          key={generation.current}
          onUpdate={updateRecoveredPassword}
          message={message}
        />
      </AccessFrame>
    );
  return (
    <AccessFrame>
      <SignIn
        mode={client.mode}
        onContinue={continueToTenantResolution}
        onRecover={requestPasswordRecovery}
        message={message}
        credentialFailure={credentialFailure}
        onDismissCredentialFailure={() => setCredentialFailure(false)}
        canRetryWorkspace={canRetryWorkspace}
        onRetryWorkspace={resolveAuthenticatedWorkspace}
        onHelp={() =>
          setMessage(
            "Contact your Broadview administrator if your account has not been provisioned.",
          )
        }
      />
    </AccessFrame>
  );
}

function accessMessage(error: unknown): string {
  if (typeof error === "object" && error !== null && "status" in error && error.status === 429) {
    return "Too many sign-in links were requested. Please wait a few minutes, then request one new link.";
  }
  if (error instanceof BroadviewClientError) {
    if (error.code === "UNAUTHENTICATED")
      return "A verified production session is required. Production sign-in is coordinator-owned.";
    if (error.code === "FORBIDDEN")
      return "The verified identity does not have access to this workspace.";
    if (error.code === "CONTRACT_MISMATCH")
      return "The service response could not be safely displayed. No fixture data was substituted.";
    return `${error.message}${error.correlationId ? ` Reference: ${error.correlationId}` : ""}`;
  }
  if (
    typeof error === "object" &&
    error !== null &&
    (("name" in error && error.name === "AuthSessionMissingError") ||
      ("message" in error &&
        typeof error.message === "string" &&
        error.message.toLowerCase().includes("auth session missing")))
  ) {
    return "The secure recovery session is not available. Request one new password setup link and discard older emails.";
  }
  return "Workspace access could not be completed. No fixture data was substituted.";
}

function AccessFrame({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(14,165,233,0.14),_transparent_34%),linear-gradient(135deg,#030712,#07111d)] px-4 py-8 text-foreground sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center justify-center">
        {children}
      </div>
    </main>
  );
}

function SignIn({
  mode,
  onContinue,
  onRecover,
  onHelp,
  message,
  credentialFailure,
  onDismissCredentialFailure,
  canRetryWorkspace,
  onRetryWorkspace,
}: {
  mode: "fixture" | "live";
  onContinue: (credentials: {
    method: SignInMethod;
    email: string;
    password: string;
  }) => Promise<boolean>;
  onRecover: (email: string) => Promise<boolean>;
  onHelp: () => void;
  message: string | null;
  credentialFailure: boolean;
  onDismissCredentialFailure: () => void;
  canRetryWorkspace: boolean;
  onRetryWorkspace: () => Promise<boolean>;
}) {
  const [method, setMethod] = useState<SignInMethod>("magic-link");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState<string | null>(null);
  const [recoveryEmail, setRecoveryEmail] = useState<string | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const succeeded = await onContinue({ method, email, password });
      if (succeeded && mode === "live" && method === "magic-link") setMagicLinkEmail(email);
    } finally {
      setBusy(false);
    }
  };
  const recover = async () => {
    if (!email) return;
    setBusy(true);
    try {
      if (await onRecover(email)) setRecoveryEmail(email);
    } finally {
      setBusy(false);
    }
  };
  const retryWorkspace = async () => {
    setBusy(true);
    try {
      await onRetryWorkspace();
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <section
        aria-labelledby="sign-in-title"
        className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-950/80 shadow-2xl shadow-cyan-950/30 backdrop-blur"
      >
        <div className="border-b border-slate-800 px-7 py-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-cyan-500 text-slate-950">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <p className="text-sm font-semibold tracking-wide text-white">BROADVIEW</p>
              <p className="text-xs text-slate-400">Warranty Intelligence</p>
            </div>
          </div>
        </div>
        <div className="space-y-6 px-7 py-7">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
              Subscriber Portal
            </p>
            <h1
              id="sign-in-title"
              className="mt-2 text-2xl font-semibold tracking-tight text-white"
            >
              Welcome back
            </h1>
            <p className="mt-2 text-sm leading-6 text-slate-400">
              Sign in to access your verified subscriber workspace.
            </p>
          </div>
          <div
            role="note"
            className="rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-xs leading-5 text-amber-100"
          >
            {mode === "fixture" ? (
              <>
                <strong>Fixture-only flow.</strong> This visual prototype does not send email,
                validate credentials, or grant access. Production sign-in is coordinator-owned.
              </>
            ) : (
              <>
                <strong>Secure sign-in.</strong> Supabase verifies your session; the Broadview API
                independently enforces tenant membership and permissions.
              </>
            )}
          </div>
          <div className="grid grid-cols-2 rounded-lg border border-slate-800 bg-slate-900/80 p-1">
            <button
              type="button"
              onClick={() => setMethod("magic-link")}
              className={`rounded-md px-3 py-2 text-sm font-medium ${method === "magic-link" ? "bg-cyan-500 text-slate-950" : "text-slate-300 hover:text-white"}`}
            >
              Magic link
            </button>
            <button
              type="button"
              onClick={() => setMethod("password")}
              className={`rounded-md px-3 py-2 text-sm font-medium ${method === "password" ? "bg-cyan-500 text-slate-950" : "text-slate-300 hover:text-white"}`}
            >
              Email & password
            </button>
          </div>
          <form className="space-y-4" onSubmit={submit}>
            <label className="block text-sm font-medium text-slate-200">
              Work email
              <input
                required
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="name@company.com"
                className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-white outline-none ring-cyan-400 transition placeholder:text-slate-500 focus:border-cyan-400 focus:ring-2"
              />
            </label>
            {method === "password" && (
              <>
                <label className="block text-sm font-medium text-slate-200">
                  Password
                  <input
                    required
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter your password"
                    className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-white outline-none ring-cyan-400 transition placeholder:text-slate-500 focus:border-cyan-400 focus:ring-2"
                  />
                </label>
                <button
                  type="button"
                  disabled={busy || mode !== "live" || !email}
                  onClick={() => void recover()}
                  className="text-left text-xs text-cyan-300 underline-offset-4 hover:underline disabled:text-slate-600"
                >
                  Set or reset your password securely
                </button>
              </>
            )}
            <button
              disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-400 px-4 py-3 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300 disabled:cursor-wait disabled:opacity-70"
            >
              {method === "magic-link" ? (
                <Mail className="h-4 w-4" />
              ) : (
                <KeyRound className="h-4 w-4" />
              )}
              {busy
                ? method === "magic-link"
                  ? "Sending secure link…"
                  : "Signing in…"
                : method === "magic-link"
                  ? "Continue with magic link"
                  : "Continue securely"}
              <ArrowRight className="h-4 w-4" />
            </button>
          </form>
          <div className="flex items-center gap-3 text-xs text-slate-500">
            <span className="h-px flex-1 bg-slate-800" />
            SSO availability is coordinator-configured
            <span className="h-px flex-1 bg-slate-800" />
          </div>
          <button
            type="button"
            disabled
            title="SSO is not configured in the fixture deployment"
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-4 py-2.5 text-sm font-medium text-slate-500"
          >
            Continue with SSO
          </button>
          {canRetryWorkspace && (
            <button
              type="button"
              disabled={busy}
              onClick={() => void retryWorkspace()}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-cyan-400/50 bg-cyan-400/10 px-4 py-2.5 text-sm font-semibold text-cyan-200 hover:bg-cyan-400/15 disabled:opacity-60"
            >
              <ShieldCheck className="h-4 w-4" />
              {busy ? "Retrying workspace…" : "Retry workspace access"}
            </button>
          )}
          <button
            type="button"
            onClick={onHelp}
            className="w-full text-center text-xs text-cyan-300 underline-offset-4 hover:underline"
          >
            Need account or sign-in help?
          </button>
          {message && (
            <p
              role="status"
              className="rounded-lg bg-slate-900 px-3 py-2 text-xs leading-5 text-slate-300"
            >
              {message}
            </p>
          )}
        </div>
      </section>
      {magicLinkEmail && (
        <AuthEmailSuccessModal
          title="Secure sign-in link sent"
          email={magicLinkEmail}
          detail="Open the link in the same browser and device to restore your secure session."
          onClose={() => setMagicLinkEmail(null)}
        />
      )}
      {recoveryEmail && (
        <AuthEmailSuccessModal
          title="Secure password setup link sent"
          email={recoveryEmail}
          detail="Open the newest link, then choose your new password. Older recovery links should be discarded."
          onClose={() => setRecoveryEmail(null)}
        />
      )}
      {credentialFailure && <AuthFailureModal onClose={onDismissCredentialFailure} />}
    </>
  );
}

function AuthFailureModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="auth-failure-title"
      aria-describedby="auth-failure-description"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 backdrop-blur-sm"
    >
      <section className="w-full max-w-sm rounded-2xl border border-amber-400/35 bg-slate-950 p-7 text-center shadow-2xl shadow-cyan-950/40">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-amber-400/15">
          <CircleAlert className="h-7 w-7 text-amber-300" />
        </div>
        <p className="mt-4 text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
          Broadview secure sign-in
        </p>
        <h2 id="auth-failure-title" className="mt-2 text-xl font-semibold text-white">
          We couldn't sign you in
        </h2>
        <p id="auth-failure-description" className="mt-3 text-sm leading-6 text-slate-300">
          The email or password wasn't recognized. Check your details and try again, or use the
          secure password setup link.
        </p>
        <button
          type="button"
          autoFocus
          onClick={onClose}
          className="mt-6 w-full rounded-lg bg-cyan-400 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-cyan-300"
        >
          Try again
        </button>
      </section>
    </div>
  );
}

function AuthEmailSuccessModal({
  title,
  email,
  detail,
  onClose,
}: {
  title: string;
  email: string;
  detail: string;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="auth-email-success-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 px-4 backdrop-blur-sm"
    >
      <section className="w-full max-w-sm rounded-2xl border border-cyan-400/30 bg-slate-950 p-7 text-center shadow-2xl shadow-cyan-950/40">
        <CheckCircle2 className="mx-auto h-11 w-11 text-cyan-300" />
        <h2 id="auth-email-success-title" className="mt-4 text-xl font-semibold text-white">
          {title}
        </h2>
        <p className="mt-3 text-sm leading-6 text-slate-300">
          We sent instructions to <strong className="text-white">{email}</strong>. {detail}
        </p>
        <button
          type="button"
          onClick={onClose}
          className="mt-6 w-full rounded-lg bg-cyan-400 px-4 py-2.5 text-sm font-semibold text-slate-950 hover:bg-cyan-300"
        >
          Return to sign in
        </button>
      </section>
    </div>
  );
}

function PasswordRecovery({
  onUpdate,
  message,
}: {
  onUpdate: (password: string) => Promise<void>;
  message: string | null;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const tooShort =
    (password.length > 0 && password.length < 12) ||
    (confirmation.length > 0 && confirmation.length < 12);
  const mismatch = confirmation.length > 0 && password !== confirmation;
  const canSubmit = password.length >= 12 && confirmation.length >= 12 && !mismatch;

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await onUpdate(password);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-950/90 p-7 shadow-2xl">
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
        Secure account recovery
      </p>
      <h1 className="mt-2 text-2xl font-semibold text-white">Choose a new password</h1>
      <p className="mt-2 text-sm leading-6 text-slate-400">
        Your recovery link restored a verified session. Use at least 12 characters; the password is
        sent only to Supabase Auth and is never stored by Broadview.
      </p>
      <form className="mt-6 space-y-4" onSubmit={submit}>
        <label className="block text-sm font-medium text-slate-200">
          New password
          <input
            required
            minLength={12}
            autoComplete="new-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-white outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400"
          />
        </label>
        <label className="block text-sm font-medium text-slate-200">
          Confirm new password
          <input
            required
            minLength={12}
            autoComplete="new-password"
            type="password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            className="mt-2 w-full rounded-lg border border-slate-700 bg-slate-900 px-3 py-2.5 text-white outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-400"
          />
        </label>
        {tooShort && (
          <p role="status" className="text-xs text-amber-200">
            Use at least 12 characters in both password fields.
          </p>
        )}
        {!tooShort && mismatch && (
          <p role="status" className="text-xs text-amber-200">
            The passwords do not match.
          </p>
        )}
        {message && <p className="text-xs leading-5 text-amber-100">{message}</p>}
        <button
          disabled={busy || !canSubmit}
          className="flex w-full items-center justify-center gap-2 rounded-lg bg-cyan-400 px-4 py-3 text-sm font-semibold text-slate-950 hover:bg-cyan-300 disabled:opacity-60"
        >
          <KeyRound className="h-4 w-4" />
          {busy ? "Updating securely…" : "Update password and continue"}
        </button>
      </form>
    </section>
  );
}

function LoadingState() {
  return (
    <section
      aria-live="polite"
      className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-950/80 p-8 text-center shadow-2xl"
    >
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border-2 border-cyan-300 border-t-transparent motion-safe:animate-spin">
        <LockKeyhole className="h-5 w-5 text-cyan-300" />
      </div>
      <h1 className="mt-5 text-xl font-semibold text-white">Checking eligible workspaces</h1>
      <p className="mt-2 text-sm leading-6 text-slate-400">
        Resolving verified tenant memberships and effective permissions.
      </p>
    </section>
  );
}

function DivisionSelection({
  tenants,
  onSelect,
}: {
  tenants: readonly TenantAccess[];
  onSelect: (tenant: TenantAccess) => Promise<void>;
}) {
  return (
    <section
      aria-labelledby="division-title"
      className="w-full max-w-2xl rounded-2xl border border-slate-700 bg-slate-950/85 p-7 shadow-2xl"
    >
      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-cyan-300">
        Subscriber Portal
      </p>
      <h1 id="division-title" className="mt-2 text-2xl font-semibold text-white">
        Choose an eligible Division
      </h1>
      <p className="mt-2 text-sm leading-6 text-slate-400">
        Select the Division workspace you want to enter. Access is sourced from your active
        Broadview memberships and revalidated by the API.
      </p>
      <div className="mt-6 space-y-3">
        {tenants.map((tenant) => (
          <button
            type="button"
            key={tenant.tenantId}
            onClick={() => void onSelect(tenant)}
            className="flex w-full items-center gap-4 rounded-xl border border-slate-700 bg-slate-900/80 p-4 text-left transition hover:border-cyan-400 hover:bg-slate-900 focus:outline-none focus:ring-2 focus:ring-cyan-400"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-cyan-400/15 text-cyan-300">
              <Building2 className="h-5 w-5" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate font-semibold text-white">{tenant.tenantName}</span>
              <span className="mt-1 block truncate text-xs text-slate-400">
                Division workspace · Subscriber account: {tenant.subscriberAccountId}
              </span>
            </span>
            <ArrowRight className="h-5 w-5 text-cyan-300" />
          </button>
        ))}
      </div>
      <p className="mt-5 text-xs text-amber-100">
        The current API represents eligible Divisions as tenant workspaces. A first-class
        Organization/Division selector will replace this compatibility label when its contract is
        available.
      </p>
    </section>
  );
}

function NoAccess({ onReturn }: { onReturn: () => void }) {
  return (
    <section
      aria-labelledby="no-access-title"
      className="w-full max-w-md rounded-2xl border border-slate-700 bg-slate-950/85 p-8 text-center shadow-2xl"
    >
      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-400/15 text-amber-200">
        <LockKeyhole className="h-6 w-6" />
      </div>
      <h1 id="no-access-title" className="mt-5 text-xl font-semibold text-white">
        No eligible Division workspace
      </h1>
      <p className="mt-2 text-sm leading-6 text-slate-400">
        Your verified identity has no active Division workspace membership. Request access through
        your Broadview administrator.
      </p>
      <button
        type="button"
        onClick={onReturn}
        className="mt-6 inline-flex items-center gap-2 rounded-lg border border-slate-700 px-4 py-2.5 text-sm font-semibold text-slate-200 hover:border-cyan-400"
      >
        <CheckCircle2 className="h-4 w-4" />
        Return to sign-in
      </button>
    </section>
  );
}
